/**
 * The Symagic Server Repeat entity.
 * @author Tom Verschoore de la Houssaije
 */
import * as moment from 'moment';
import { RepeatValidation } from './repeat-validation';
/**
 * The Symagic Server Repeat entity.
 */
var Repeat = /** @class */ (function () {
    function Repeat() {
    }
    Object.defineProperty(Repeat.prototype, "UntilDate", {
        /**
         * Gets the end date of the repeat cycle is equivalent to cycles.
         * An until date of 9999/12/31 is regarded as infinitely large.
         * @memberOf Repeat
         */
        get: function () {
            if (this.Kind === 'ONCE') {
                return null;
            }
            return this._untilDate;
        },
        /**
         * Sets the end date of the repeat cycle is equivalent to cycles.
         * An until date of 9999/12/31 is regarded as infinitely large.
         * @memberOf Repeat
         */
        set: function (value) {
            this._untilDate = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Checks whether a given date is infinite.
     * @returns True when date is infinite.
     */
    Repeat.isInfinity = function (date) {
        if (date == null) {
            return false;
        }
        return date.getUTCFullYear() === 9999;
    };
    /**
     * Checks whether or not this repeat can be used with given start.
     * @param {Date} startOrUntil To check this repeat with.
     * @param {boolean} isStart Flag to indicate we are validating a start date not an until date.
     * @returns {RepeatValidation} RepeatValidation.Ok when valid otherwise value indicating what is wrong.
     */
    Repeat.prototype.validateWith = function (startOrUntil, isStart) {
        if (isStart === void 0) { isStart = true; }
        if (this.Kind === 'ONCE') {
            return RepeatValidation.Ok;
        }
        // Wens van WJ https://github.com/Symagic/html5/issues/999#issuecomment-389877101
        var testDate = new Date(startOrUntil.getFullYear(), startOrUntil.getMonth(), startOrUntil.getDate());
        if (isStart) {
            if (this.UntilDate < testDate) {
                return RepeatValidation.UntilDateBeforeStart;
            }
        }
        else {
            var start = this.Start;
            if (this.Owner) {
                start = this.Owner['Start'];
            }
            if (testDate < start) {
                return RepeatValidation.UntilDateBeforeStart;
            }
        }
        switch (this.Kind) {
            case 'WEEKLY':
                if (!this.checkDayOfWeekWith(startOrUntil)) {
                    return RepeatValidation.WeekDayMismatch;
                }
                return RepeatValidation.Ok;
            case 'MONTHLY RELATIVE':
                if (!this.checkDayOfWeekWith(startOrUntil)) {
                    return RepeatValidation.WeekDayMismatch;
                }
                if (!this.checkWeekDayOfMonthWith(startOrUntil)) {
                    return RepeatValidation.OccurrenceOrderMismatch;
                }
                return RepeatValidation.Ok;
            case 'YEARLY RELATIVE':
                if (!this.checkDayOfWeekWith(startOrUntil)) {
                    return RepeatValidation.WeekDayMismatch;
                }
                if (!this.checkWeekDayOfMonthWith(startOrUntil)) {
                    return RepeatValidation.OccurrenceOrderMismatch;
                }
                if (this.Factor !== startOrUntil.getMonth() + 1) {
                    return RepeatValidation.MonthMismatch;
                }
                return RepeatValidation.Ok;
            case 'MONTHLY ABSOLUTE':
                var date = Number.parseInt(this.Days);
                if (date !== startOrUntil.getDate()) {
                    if (date === 31 || date === 30 || date === 29) {
                        var test = new Date(startOrUntil.getTime() + 86400000);
                        if (test.getDate() === 1 && test.getMonth() > startOrUntil.getMonth()) {
                            return RepeatValidation.Ok;
                        }
                    }
                    return RepeatValidation.DateMismatch;
                }
                return RepeatValidation.Ok;
            case 'YEARLY ABSOLUTE':
                date = Number.parseInt(this.Days);
                if (date !== startOrUntil.getDate()) {
                    if (date === 31 || date === 30 || date === 29) {
                        var test = new Date(startOrUntil.getTime() + 86400000);
                        if (test.getDate() === 1 && test.getMonth() > startOrUntil.getMonth()) {
                            if (this.Factor !== startOrUntil.getMonth() + 1) {
                                return RepeatValidation.MonthMismatch;
                            }
                            else {
                                return RepeatValidation.Ok;
                            }
                        }
                    }
                    return RepeatValidation.DateMismatch;
                }
                if (this.Factor !== startOrUntil.getMonth() + 1) {
                    return RepeatValidation.MonthMismatch;
                }
                return RepeatValidation.Ok;
            case 'DAILY':
                return RepeatValidation.Ok;
        }
        return RepeatValidation.Ok;
    };
    /**
     * Checks whether or not given start complies with the day of week of this repeat.
     * @private
     * @param {Date} start The start to check.
     * @returns {boolean} True when valid otherwise false.
     */
    Repeat.prototype.checkDayOfWeekWith = function (start) {
        var daysArray = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        var days = this.Days.split(' ');
        if (days.indexOf(daysArray[start.getDay()]) === -1) {
            return false;
        }
        return true;
    };
    /**
     * Checks whether or not day of week of given start complies with the occurrence order in month of this repeat.
     * @private
     * @param {Date} start The start to check.
     * @returns {boolean} True when valid otherwise false.
     */
    Repeat.prototype.checkWeekDayOfMonthWith = function (start) {
        var odersArray = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'];
        var oderIndex = odersArray.indexOf(this.Order);
        if (oderIndex === -1) {
            return false;
        }
        var startMoment = moment(start);
        var monthOfStart = startMoment.month();
        if (oderIndex === 4) { // Check if last
            return startMoment.add(7, 'days').month() !== monthOfStart;
        }
        var firstOfMonth = startMoment.subtract(7 * oderIndex, 'days');
        if (firstOfMonth.month() !== monthOfStart) {
            return false;
        }
        return firstOfMonth.subtract(7, 'days').month() !== monthOfStart;
    };
    return Repeat;
}());
export { Repeat };
